import { find, map, get, isEmpty, forEach, includes } from 'lodash';
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import { AnalyticsConstants } from './analyticsConstants.js';
import { MDTAnalytics } from '../services/mdtanalytics.js';

/**
 * We will assume that a string is translatable when:
 * - Has no spaces on the sentence
 * @param string
 * @returns {boolean}
 */
export const isTranslatableLabel = (string) => {
    return !string.trim().includes(' ');
};

/**
 * @param {Array} userRoles
 * @returns {String} String of assigned user roles to perticular user.
 */
export const getUserRoles = (userRoles) => {
    const rolesArray =
        (userRoles.length && find(userRoles, (k) => k.appId === appSettings.appid)) || [];
    const roles =
        (rolesArray.roles && rolesArray.roles.length > 0 && map(rolesArray.roles, (o) => o.name)) ||
        '';
    const filteredRoles = Array.isArray(roles) && roles.filter((el, i, a) => i === a.indexOf(el));
    return (filteredRoles && filteredRoles.join()) || '';
};

export const FormatPhoneNumber = (value) => {
    const phoneNumber = parsePhoneNumberFromString(value);
    let formattedNumber = value;
    if (phoneNumber) {
        const val = phoneNumber.country === 'US' ? phoneNumber.nationalNumber : value; // Shows phoneNumber with Country Calling Code except for US
        const asYouType = new AsYouType(phoneNumber.country || 'US');
        formattedNumber = asYouType.input(val);
    }
    return formattedNumber;
};

export const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp(`[\\?&]${name}=([^&#]*)`);
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export const getLabelsForTracking = (data, toFilter, joiner = ':') => {
    if (Array.isArray(data) && data.length) {
        return data
            .map((obj) => get(obj, toFilter, ''))
            .filter((e) => e !== '')
            .join(joiner);
    }
    return '';
};

export const getExtendedLabelsForTracking = (
    data,
    propOneToFilter,
    propTwoToFilter,
    seperator = '|',
    joiner = ' : '
) => {
    if (Array.isArray(data) && data.length) {
        return data
            .map(
                (obj) =>
                    `${get(obj, propOneToFilter, '')} ${seperator} ${get(obj, propTwoToFilter, '')}`
            )
            .filter((e) => e !== '')
            .join(joiner);
    }
    return '';
};

export const getCustomer = (userId) => {
    const customerStr = localStorage.getItem('customer');
    if (customerStr == null) {
        return 'NA';
    }
    const parsedCustomersArr = customerStr.length && JSON.parse(customerStr);
    const customer =
        Array.isArray(parsedCustomersArr) &&
        parsedCustomersArr.filter((obj) => obj.userid === userId);
    return get(customer, '[0]', {});
};

const getAnalyticsConstantlabel = (obj, type) => {
    try {
        const { pageName, label, name } = obj;
        return pageName
            ? AnalyticsConstants[type][name](pageName, label)
            : AnalyticsConstants[type][name](label);
    } catch (err) {
        console.error(err);
    }
};

const processTrackingLabelString = (obj, type) => {
    return typeof obj === 'object'
        ? getAnalyticsConstantlabel(obj, type)
        : AnalyticsConstants[type][obj];
};

/** @typedef {'eventLabel'|'eventCategory'|'eventName'|'eventValue'|'eventAction'} TranslatableFields */
/**
 * Send tracking data to MDTAnalytics
 * @param param {Object} - Object containing the data to track
 * @param param.translatableFields {Array[TranslatableFields]} - Array of translatable fields
 */
export const sendEventTrackingData = (param) => {
    const {
        processLabels = true,
        processActions = true,
        customInfo = {},
        translatableFields = [],
    } = param;
    const labelObj = get(param, 'eventLabel');
    const actionObj = get(param, 'eventAction');

    const label = processLabels ? processTrackingLabelString(labelObj, 'labels') : labelObj;
    const action = processActions ? processTrackingLabelString(actionObj, 'actions') : actionObj;

    const eventInfo = {
        eventName: AnalyticsConstants.names[get(param, 'eventName', 'dataTrack')],
        eventCategory: AnalyticsConstants.categories[get(param, 'eventCategory')] || 'NA',
        eventAction: action || 'NA',
        eventLabel: label || 'NA',
        eventValue: AnalyticsConstants.values[get(param, 'eventValue', 'one')],
        ...customInfo,
    };

    if (
        eventInfo.eventAction === 'NA' ||
        eventInfo.eventLabel === 'NA' ||
        eventInfo.eventCategory === 'NA'
    ) {
        console.warn(
            'sendEventTrackingData: The following eventInfo yield a NA value, this should not happen:',
            { eventInfo, param }
        );
        return;
    }

    // NOTE: Only allow to translate values that are part of the reporting fields 20221018:Alevale
    if (!isEmpty(translatableFields)) {
        if (window.next18 && window.next18.getFixedT) {
            // NOTE: We are going to be sending to the Analytics the keys translated to english 20221018:Alevale
            const en = window.next18.getFixedT('en');
            // NOTE: Overwrite the default eventInfo with the translated values of it 20221018:Alevale
            forEach(translatableFields, (keyToTranslate) => {
                if (includes(Object.keys(eventInfo), keyToTranslate)) {
                    eventInfo[keyToTranslate] = en(get(param, keyToTranslate));
                }
            });
        }
    }

    MDTAnalytics.track(
        {
            eventInfo,
        },
        true
    );
};

/**
 * This translates a key to English so Analytics can make aggregations.
 * @param key {String} - The key to translate
 * @param params {Object} - Optional parameters for translation interpolation
 * @returns {Promise<*>} The English translation of the key.
 */
export const getEnglishTranslation = async (key, params = {}) => {
    if (window.next18 && window.next18.getFixedT) {
        const en = window.next18.getFixedT('en');
        if (en(key, params) === key) {
            await window.next18.loadLanguages('en');
            const retryEn = window.next18.getFixedT('en');
            return retryEn(key, params);
        }
        return en(key, params);
    }
    return key;
};

export const getUserType = (loginId) => {
    const isValidEmail = new RegExp(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/);
    const isMedtronicEmail = new RegExp('@medtronic.com\s*', 'i');

    let isInternalUser = false;

    if (
        (isValidEmail.test(loginId) && isMedtronicEmail.test(loginId)) ||
        !isValidEmail.test(loginId)
    ) {
        isInternalUser = true;
    }

    return isInternalUser ? 'Internal' : 'External';
};

export const waitFor = (time) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve(`The timer of ${time} ms finished`);
        }, time);
    });
};

export const alertDocument = (supplierInfos) => {
    let alertObj;
    if (supplierInfos && supplierInfos.documents && supplierInfos.documents.length) {
        supplierInfos.documents.map((document) => {
            if (document && document.componentType && document.componentType.key === 'ALERT') {
                alertObj = {
                    content: document.content ? document.content : null,
                    type: document.mdtType ? document.mdtType : null,
                };
            } else {
                alertObj = {
                    content: null,
                    type: null,
                };
            }
        });
    } else {
        alertObj = {
            content: null,
            type: null,
        };
    }
    return alertObj;
};

export const numberFormat = (value, language = 'en-US', decimalDigits = 0) => {
    let number;
    try {
        number = Number(value).toLocaleString(language.replace('_', '-'), {
            minimumFractionDigits: decimalDigits,
            maximumFractionDigits: decimalDigits,
        });
    } catch (e) {
        number = '';
    }
    return number;
};

export const pushIntoDigitalData = (data) => {
    try {
        window.digitalData.transactionProducts.push(data);
        sessionStorage.setItem(
            'transactions',
            JSON.stringify(window.digitalData.transactionProducts)
        );
    } catch (err) {
        console.log(err);
    }
};

export const pushIntoDigitalDataTransaction = (data) => {
    try {
        window.digitalData.transaction.push(data);
        sessionStorage.setItem('transaction', JSON.stringify(window.digitalData.transaction));
    } catch (err) {
        console.log(err);
    }
};

export const formatDataUsingSeparator = (data) => {
    return data.reduce(function (acc, curr) {
        for (const key in curr) {
            acc[key] = `${acc[key]}|${curr[key]}`;
        }
        return acc;
    });
};

export const appendScript = (scriptToAppend, async = true) => {
    const script = document.createElement('script');
    script.src = scriptToAppend;
    script.async = async;
    document.body.appendChild(script);
};

/**
 * Sanitizes and retrieves the value of a specified property from an object.
 *
 * Given the BE doesn't seem to have a stable naming convention for the keys, we are
 * normalizing the keys to lowercase and also normalizing the values to lowercase
 * if they are strings. Yes, this is a PITA, but it'll be part of the cleanup EPIC
 *
 * @param {Object} obj - The object to search for the key.
 * @param {string} key - The name of the key whose value needs to be sanitized and retrieved.
 * @returns {string|*|undefined} The sanitized value of the key as a lowercase string (if applicable),
 *                               or the unmodified value if it is not a string, or undefined
 *                               if the key does not exist.
 */
export const sanitizeBEProperties = (obj, key) => {
    if (!obj || typeof obj !== 'object') return obj;

    if (obj.hasOwnProperty(key)) {
        // If the key exists in the object, return its value after sanitizing it to lower case
        const value = obj[key];
        if (typeof value === 'string') {
            // If the value is a string, return it as lower case
            return value.toLowerCase();
        }
    }

    // Check if the key exists in the object, ignoring case
    const foundKey = Object.keys(obj).find((k) => k.toLowerCase() === key.toLowerCase());

    // If the key is found, return its value
    if (foundKey) {
        const value = obj[foundKey];
        // If the value is a string, return it as is
        if (typeof value === 'string') {
            return value.toLowerCase();
        }
        // If the value is an object or array, return it as is
        return value;
    }

    // If the key is not found, return undefined
    return undefined;
};

/**
 * Extracts and normalizes the application path from an access token's payload.
 * @param {Object} accessToken - The access token containing app routing information
 */

export const getAppUrl = (accessToken) => {
    const { defaultApp, app } = accessToken || {};
    const appPath =
        app?.reduce((acc, curr) => {
            const appName = curr.split('/').pop();
            return appName && appName !== '/'
                ? { ...acc, [appName.toLowerCase()]: appName.toLowerCase() }
                : acc;
        }, {})[defaultApp?.toLowerCase()] || defaultApp;
    return appPath.toLowerCase();
};

/**
 * Checks if any facet in the array is selected.
 * Handles invalid inputs gracefully.
 *
 * @param {Array<object> | null | undefined} facets - The list of facets to check.
 * @param {string} key - The property name to check for truthiness (default: "selected").
 * @returns {boolean} True if at least one facet has the property truthy, otherwise false.
 */
export const isAnyFacetSelected = (facets = [], key = 'selected') => {
    try {
        if (!Array.isArray(facets)) return false;

        return facets.some((facet) => {
            if (facet && typeof facet === 'object') {
                const value = facet[key];
                return Boolean(value);
            }
            return false;
        });
    } catch (error) {
        console.error('Error checking facets:', error);
        return false;
    }
};

/**
 * Formats a string representing a duration into "HH:MM:SS" format.
 *
 * @param {string|number} val - The input duration.
 * @returns {string} - The duration formatted as "HH:MM:SS".
 */
export const formatDuration = (val) => {
    if (typeof val === 'number') val = val.toString();

    // Replace colon with dot for consistent splitting
    const raw = val.replace(/:/g, '.').replace(/[^0-9.]/g, '');
    let [h = '0', m = '0', s = '0'] = raw.split('.');
    if (raw.includes('.') && raw.split('.').length === 2 && parseInt(m) > 0 && parseInt(m) < 60) {
        m = m.padStart(2, '0');
    } else if (raw.includes('.') && raw.split('.').length === 2 && parseInt(m) >= 60) {
        const decimal = parseFloat(`0.${m}`);
        m = Math.round(decimal * 60)
            .toString()
            .padStart(2, '0');
    } else {
        m = m.padStart(2, '0');
    }
    h = h.padStart(2, '0');
    s = s.padStart(2, '0');

    return `${h}:${m}:${s}`;
};

/**
 * Returns true when the "Show all accounts" toggle must be hidden.
 *
 * Rule: hide when the user is external AND has more than 100 assigned roles.
 * Both conditions must be true simultaneously.
 *
 * Data source: state.meDetails.user (populated from /api/common/users/userinfo)
 *   - isinternal:      "True" | "False"  (string, not boolean)
 *   - isRolesCountMax: "true" | "false"  (string, not boolean — set by DotNet when role count > 100)
 *
 * @param {Object} state - Redux state
 * @returns {boolean}
 */
export const isExternalUserWithMaxRoles = (state) => {
    const isInternal = get(state, 'meDetails.user.isInternal', 'True');
    const isRolesCountMax = get(state, 'meDetails.user.isRolesCountMax', 'false');
    return isInternal.toLowerCase() === 'false' && isRolesCountMax.toLowerCase() === 'true';
};
